import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TestResult } from '../../../shared/test.types';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import {
  DigitSpanResult,
  EchoDirection,
  EchoTrialResult,
} from '../../sharp-v2.types';
import { SharpAudioService } from '../../services/sharp-audio.service';
import { SharpScoringService } from '../../services/sharp-scoring.service';

type Phase = 'intro' | 'show' | 'input' | 'between' | 'interrupted' | 'done';

// Digit Span (handoff 2026-08-17, part C). Rebuilt from the old spatial 9-pad
// "chamber" to the classic paradigm: one big digit shown at a time in the centre,
// answer slots laid out in advance (one per digit) so the length is known before
// the first digit appears, and a 1-9 keypad for entry. Everything digit-related is
// LTR (slots, keypad, demo cards) - the first digit typed lands on the left, so a
// mirrored layout would teach the reverse order and add typing errors to a memory
// score. WAIS-IV: TWO attempts per span (new sequence each), stop only after both
// fail; a correct first attempt skips to the next span. A span-2 warmup precedes
// the forward direction (not scored). (Danna 2026-08-17 revision - one attempt +
// cap 5 meant a single typo zeroed a direction and everyone hit the ceiling.)
const CFG = {
  digitMs: 750,
  isiMs: 220,
  fwdStart: 3,
  fwdMax: 6,
  bwdStart: 2,
  bwdMax: 5,
  trialsPerSpan: 2, // two attempts per span before the direction can stop
  warmupSpan: 2, // span-2 familiarisation before each direction
};

@Component({
  selector: 'app-echo-chamber',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './echo-chamber.component.html',
  styleUrls: ['./echo-chamber.component.scss'],
})
export class EchoChamberComponent implements OnInit, OnDestroy {
  @Input() testId = 'echo-chamber';
  @Output() testComplete = new EventEmitter<TestResult>();

  phase: Phase = 'intro';
  direction: EchoDirection = 'forward';
  span = CFG.fwdStart;
  warmup = false; // span-2 familiarisation before each direction (not scored)
  /** a trial that was interrupted, waiting to be replayed rather than scored */
  interrupted = false;
  centerDigit = '';
  input: number[] = [];
  readonly keypad = [1, 2, 3, 4, 5, 6, 7, 8, 9];

  private seq: number[] = [];
  private best = 0;
  private attempt = 0; // 0 or 1: which of the two attempts at the current span
  private accepting = false;
  private fwdBest = 0;
  private fwdTrials: EchoTrialResult[] = [];
  private bwdTrials: EchoTrialResult[] = [];
  private startTime = 0;
  private timers: any[] = [];
  private done = false;

  constructor(
    private audio: SharpAudioService,
    private scoring: SharpScoringService
  ) {}

  private readonly onVisibility = () => {
    if (document.visibilityState === 'hidden') this.interrupt();
  };

  ngOnInit(): void {
    this.startTime = Date.now();
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  ngOnDestroy(): void {
    this.clearTimers();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /**
   * Correction brief 20.6 / 3.2: a call, a notification or an app switch in the
   * middle of a sequence means she did not see part of it, but the trial was
   * scored anyway and a missed digit reads as a shorter memory span. An
   * interrupted trial is now thrown away and offered again at the same length -
   * nothing is recorded, so re-running it cannot help or hurt her score either.
   */
  private interrupt(): void {
    if (this.done) return;
    if (this.phase !== 'show' && this.phase !== 'input') return;
    this.clearTimers();
    this.accepting = false;
    this.centerDigit = '';
    this.input = [];
    this.interrupted = true;
    this.phase = 'interrupted';
  }

  /** replay the same span with a fresh sequence; nothing was recorded */
  resumeAfterInterrupt(): void {
    if (!this.interrupted) return;
    this.interrupted = false;
    this.play();
  }

  private clearTimers(): void {
    this.timers.forEach((t) => clearTimeout(t));
    this.timers = [];
  }

  /** Slots laid out one per digit, the next one flagged (handoff part C). */
  get slots(): { value: string; next: boolean }[] {
    return Array.from({ length: this.span }, (_, i) => ({
      value: this.input[i] != null ? String(this.input[i]) : '',
      next: i === this.input.length && this.phase === 'input',
    }));
  }

  get spanParams() {
    return { n: this.span };
  }

  get taskTitleKey(): string {
    return this.direction === 'forward'
      ? 'cognitive.sharpV2.echo.taskTitleFwd'
      : 'cognitive.sharpV2.echo.taskTitleBwd';
  }

  get hintKey(): string {
    if (this.phase === 'show') return 'cognitive.sharpV2.echo.showHint';
    // Backward input names the direction so "now type" can't be read as forward
    // (Danna danna-217): "now type, from the end to the start".
    return this.direction === 'backward'
      ? 'cognitive.sharpV2.echo.inputHintBwd'
      : 'cognitive.sharpV2.echo.inputHint';
  }

  get keypadOff(): boolean {
    return this.phase !== 'input';
  }

  beginForward(): void {
    this.audio.prime();
    // Forward opens with a span-2 warmup (familiarisation, not scored).
    this.startDirection('forward', CFG.warmupSpan, true);
  }

  beginBackward(): void {
    this.audio.prime();
    // Correction brief 20.3: reversing the order is the hardest thing this task
    // asks, and it used to be explained and then scored on the very next
    // sequence. Backward now gets the same unscored familiarisation forward has,
    // so the first real trial is not the one where she learns the rule.
    this.startDirection('backward', CFG.warmupSpan, true);
  }

  private startDirection(dir: EchoDirection, span: number, warmup: boolean): void {
    this.direction = dir;
    this.warmup = warmup;
    this.span = span;
    this.best = 0;
    this.attempt = 0;
    this.play();
  }

  private play(): void {
    this.clearTimers();
    this.input = [];
    this.accepting = false;
    this.centerDigit = '';
    this.phase = 'show';
    // Build the sequence with no adjacent repeats.
    this.seq = [];
    let prev = -1;
    for (let i = 0; i < this.span; i++) {
      let x: number;
      do {
        x = this.scoring.randInt(1, 9);
      } while (x === prev);
      this.seq.push(x);
      prev = x;
    }
    let i = 0;
    const step = () => {
      if (i >= this.seq.length) {
        this.centerDigit = '';
        this.phase = 'input';
        this.accepting = true;
        return;
      }
      this.centerDigit = String(this.seq[i]);
      this.audio.padTone(this.seq[i]);
      const t1 = setTimeout(() => {
        this.centerDigit = '';
        const t2 = setTimeout(() => {
          i++;
          step();
        }, CFG.isiMs);
        this.timers.push(t2);
      }, CFG.digitMs);
      this.timers.push(t1);
    };
    const lead = setTimeout(step, 500);
    this.timers.push(lead);
  }

  tap(d: number): void {
    if (!this.accepting) return;
    this.input.push(d);
    this.audio.padTone(d);
    if (this.input.length === this.seq.length) {
      this.accepting = false;
      const t = setTimeout(() => this.evaluate(), 380);
      this.timers.push(t);
    }
  }

  undo(): void {
    if (!this.accepting || this.input.length === 0) return;
    this.input.pop();
  }

  @HostListener('window:keydown', ['$event'])
  onKey(e: KeyboardEvent): void {
    if (!this.accepting) return;
    if (e.key === 'Backspace') {
      this.undo();
      return;
    }
    const d = parseInt(e.key, 10);
    if (d >= 1 && d <= 9) this.tap(d);
  }

  private evaluate(): void {
    const target =
      this.direction === 'forward' ? this.seq : [...this.seq].reverse();
    const ok = this.input.join('') === target.join('');

    // Warmup (forward only, span 2): not scored, not recorded. Any answer starts
    // the real forward test at fwdStart.
    if (this.warmup) {
      this.attempt = 0;
      // The span only grows when the next sequence actually starts. Changing it
      // here would re-render the answer row with an extra empty slot for the
      // whole pause (Danna danna-1570).
      const t = setTimeout(() => {
        this.warmup = false;
        this.span =
          this.direction === 'forward' ? CFG.fwdStart : CFG.bwdStart;
        this.play();
      }, 450);
      this.timers.push(t);
      return;
    }

    const trial: EchoTrialResult = { length: this.span, correct: ok };
    if (this.direction === 'forward') this.fwdTrials.push(trial);
    else this.bwdTrials.push(trial);
    const max = this.direction === 'forward' ? CFG.fwdMax : CFG.bwdMax;
    if (ok) {
      this.best = this.span;
      this.attempt = 0;
      this.audio.success();
      if (this.span >= max) {
        this.finishDirection();
        return;
      }
      // Grow the span only as the next sequence starts, so the answer row she
      // just filled does not sprout an extra empty slot during the pause.
      const t = setTimeout(() => {
        this.span++;
        this.play();
      }, 450);
      this.timers.push(t);
    } else {
      this.audio.miss();
      // WAIS-IV: two attempts per span. A first miss retries the SAME span with a
      // fresh sequence; only a second miss at that span ends the direction, so a
      // single typo can no longer zero a whole direction.
      if (this.attempt < CFG.trialsPerSpan - 1) {
        this.attempt++;
        const t = setTimeout(() => this.play(), 450);
        this.timers.push(t);
      } else {
        this.finishDirection();
      }
    }
  }

  private finishDirection(): void {
    this.clearTimers();
    if (this.direction === 'forward') {
      this.fwdBest = this.best;
      this.phase = 'between';
    } else {
      this.emitResult();
    }
  }

  private emitResult(): void {
    if (this.done) return;
    this.done = true;
    this.phase = 'done';
    this.audio.success();
    const acc = (trials: EchoTrialResult[]) =>
      trials.length
        ? Math.round(
            (trials.filter((t) => t.correct).length / trials.length) * 100
          )
        : 0;
    const maxForward = this.fwdBest;
    const maxBackward = this.best;
    const response: DigitSpanResult = {
      forwardTrials: this.fwdTrials,
      backwardTrials: this.bwdTrials,
      maxForward,
      maxBackward,
      fwdAccuracy: acc(this.fwdTrials),
      bwdAccuracy: acc(this.bwdTrials),
      backwardCost: maxForward - maxBackward,
    };
    const result: TestResult = {
      testId: this.testId,
      response,
      correct: maxForward >= CFG.fwdStart && maxBackward >= CFG.bwdStart,
      responseTime: Date.now() - this.startTime,
      timestamp: Date.now(),
    };
    this.testComplete.emit(result);
  }
}
